import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { MessageSquare, Send, X, Loader2, Sparkles } from "lucide-react";
import axios from "axios";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { pollChatJob } from "../lib/chatJob";

const API = process.env.REACT_APP_BACKEND_URL;

export default function BriefChatPanel({ owner }) {
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef(null);

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async () => {
    if (!input.trim() || loading) return;
    const text = input.trim();
    setInput("");
    const userMsg = { role: "user", text, at: new Date().toISOString() };
    setMessages((m) => [...m, userMsg]);
    setLoading(true);
    try {
      const r = await axios.post(`${API}/api/brief-chat/${owner}/send-async`, {
        session_id: sessionId,
        message: text,
      });
      setSessionId(r.data.session_id);
      const j = await pollChatJob(owner, r.data.job_id);
      setMessages((m) => [...m, { role: "assistant", text: j.reply, at: j.generated_at }]);
    } catch (e) {
      setMessages((m) => [...m, {
        role: "assistant",
        text: `❌ Erreur : ${e?.response?.data?.detail || e.message || "appel LLM a échoué"}`,
        at: new Date().toISOString(),
      }]);
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setMessages([]);
    setSessionId(null);
  };

  const SUGGESTED = [
    "Je dois trim MSFT avant earnings ?",
    "Mon PF est trop Tech, quelle rotation ?",
    "Risque sur NVDA aujourd'hui ?",
    "Que faire du cash dispo ?",
  ];

  if (!open) {
    return createPortal(
      <button
        onClick={() => setOpen(true)}
        data-testid="brief-chat-open-btn"
        className="fixed bottom-[max(1.5rem,env(safe-area-inset-bottom))] right-6 z-[9999] flex items-center gap-2 px-4 py-3 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500 text-white shadow-2xl shadow-cyan-500/30 border border-cyan-400/40 transition-all"
      >
        <Sparkles className="w-5 h-5" />
        <span className="font-semibold text-sm">Pose une question</span>
      </button>,
      document.body
    );
  }

  return createPortal(
    <div
      data-testid="brief-chat-panel"
      className="fixed inset-0 sm:inset-auto sm:bottom-6 sm:right-6 z-[9999] h-[100dvh] sm:h-[600px] sm:max-h-[90dvh] sm:w-[420px] sm:max-w-[95vw] flex flex-col sm:rounded-xl bg-slate-900/95 backdrop-blur border border-cyan-500/40 shadow-2xl shadow-cyan-500/20"
    >
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-700/50 pt-[max(0.75rem,env(safe-area-inset-top))]">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-cyan-400" />
          <span className="font-semibold text-sm text-slate-100">Copilote Trading · GPT-5.5</span>
        </div>
        <div className="flex items-center gap-1">
          <button
            onClick={reset}
            data-testid="brief-chat-reset-btn"
            className="text-[10px] text-slate-400 hover:text-slate-200 px-2 py-1 rounded hover:bg-slate-800"
          >
            Reset
          </button>
          <button
            onClick={() => setOpen(false)}
            data-testid="brief-chat-close-btn"
            className="p-2 text-slate-400 hover:text-slate-200 rounded hover:bg-slate-800 min-w-[44px] min-h-[44px] flex items-center justify-center"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div
        ref={scrollRef}
        data-testid="brief-chat-messages"
        className="flex-1 overflow-y-auto px-4 py-3 space-y-3"
      >
        {messages.length === 0 && (
          <div className="space-y-3">
            <div className="text-xs text-slate-400 leading-relaxed">
              Je vois ton PF consolidé toutes brokers (Millennium, Soc.Gen, N26, T212), le Market Pulse live, le brief du jour, le scan massif, les headlines Finnhub 72h et les earnings. Pose une question concrète.
            </div>
            <div className="space-y-1.5">
              {SUGGESTED.map((s, i) => (
                <button
                  key={i}
                  onClick={() => setInput(s)}
                  className="w-full text-left text-[11px] text-cyan-300 bg-cyan-500/5 hover:bg-cyan-500/15 border border-cyan-500/20 rounded-md px-3 py-2 transition-colors"
                  data-testid={`brief-chat-suggested-${i}`}
                >
                  → {s}
                </button>
              ))}
            </div>
          </div>
        )}
        {messages.map((m, i) => (
          <div
            key={i}
            className={`text-[13px] leading-relaxed ${
              m.role === "user"
                ? "bg-blue-600/15 border border-blue-500/30 rounded-lg px-3 py-2 ml-6"
                : "bg-slate-800/60 border border-slate-700/50 rounded-lg px-3 py-2 mr-6 overflow-x-auto [&_p]:my-1.5 [&_ul]:list-disc [&_ul]:pl-4 [&_ol]:list-decimal [&_ol]:pl-4 [&_h1]:font-bold [&_h2]:font-bold [&_h3]:font-semibold [&_h1]:mt-2 [&_h2]:mt-2 [&_h3]:mt-2 [&_strong]:text-slate-50 [&_table]:text-[11px] [&_th]:text-left [&_th]:pr-2 [&_th]:border-b [&_th]:border-slate-600 [&_td]:pr-2 [&_td]:border-b [&_td]:border-slate-700/60 [&_hr]:border-slate-700 [&_hr]:my-2"
            }`}
          >
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">
              {m.role === "user" ? "Toi" : "GPT"}
            </div>
            {m.role === "user" ? <span className="whitespace-pre-wrap">{m.text}</span> : <ReactMarkdown remarkPlugins={[remarkGfm]}>{m.text}</ReactMarkdown>}
          </div>
        ))}
        {loading && (
          <div className="flex items-center gap-2 text-xs text-slate-400 mr-6">
            <Loader2 className="w-4 h-4 animate-spin text-cyan-400" />
            GPT analyse ton contexte…
          </div>
        )}
      </div>

      <div className="px-3 py-3 border-t border-slate-700/50 pb-[max(0.75rem,env(safe-area-inset-bottom))] bg-slate-900/95">
        <div className="flex items-end gap-2">
          <textarea
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            data-testid="brief-chat-input"
            rows={2}
            placeholder="Entrée pour envoyer, Shift+Entrée pour nouvelle ligne"
            className="flex-1 bg-slate-800/60 border border-slate-700 focus:border-cyan-500/50 rounded-md px-3 py-2 text-sm text-slate-100 resize-none focus:outline-none"
            style={{ fontSize: '16px' }}
          />
          <button
            onClick={send}
            disabled={loading || !input.trim()}
            data-testid="brief-chat-send-btn"
            className="min-w-[48px] min-h-[48px] flex items-center justify-center rounded-md bg-cyan-500 hover:bg-cyan-400 active:bg-cyan-600 disabled:bg-slate-700 disabled:text-slate-500 text-white transition-colors touch-manipulation"
          >
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
